/**
 * App.tsx - Main Application Entry Point
 *
 * Version History:
 * v4.39 - 2026-10-31 - BATCH-2026-10-31 row 40 (option A2): useClerkAuth reads /api/me connectSkipped; firstScreenFor gets it,
 *                      so a company that chose "Skip for now" opens the dashboard instead of looping back to Connect your
 *                      data. The owner can still open /onboarding (onboardingStillOpen) to connect a source and Finish Setup.
 * v4.36 - 2026-10-29 - EXPO-QUALIFIER-01: public /cleanup (data cleanup qualifier) and /data-safety under PublicLayout hideAuth.
 * v4.35 - 2026-10-29 - PILOT-GATE-BROWSER-ONLY-01 (row 737, Branko ruling 2026-10-28): /register no longer reads the site mode.
 *                      No invite code -> the request-early-access page (WaitlistPage) in every site mode, never Consent (the
 *                      open-mode bypass is removed). With ?invite=<code> -> Consent, which shows nothing until the server
 *                      confirms the code and sends an invalid, used or revoked code to "/". ?teamInvite= unchanged.
 *                      /demo and /demo-register redirect to "/" (DemoRegister and Demo are no longer routed, so they
 *                      create no account).
 * v4.34 - 2026-10-28 - WEBSITE-DISABLE-01 (row 781): dark mode removed for customers (Branko). ThemeContext always provides 'light',
 *                      the saved 'themeMode' browser setting is ignored and removed on load. The /sso-callback route is unchanged.
 * v4.30 - 2026-10-15 - ROW 69 (ruling 10): /api/me 409 tenant_state_incomplete (the company record has no workflow state) sets MeOutcome 'setup_incomplete';
 *                      the layout shows a translated "setup is incomplete, contact support" message. Logged with the company id.
 * v4.29 - 2026-10-13 - ONBOARDING-NOT-ENFORCED-01: every signed-in redirect now reads firstScreenFor
 *                      (services/firstScreen.ts), the one place the rule lives (AuthLanding's existing
 *                      rule, verbatim). The route table used to decide /dashboard vs /onboarding from a
 *                      hand-rolled `isOnboarding` list; it is gone. `?setup=complete` now defers to the
 *                      same function and is honoured only when /api/me already reports setup done.
 *                      /onboarding renders MemberWaiting for a non-owner whose company has not finished
 *                      setup, so a Member no longer runs the owner's onboarding steps.
 * v4.28 - 2026-10-13 - G10-B (TEAM-ACCESS-ROLES-01 / INVITE-LINK-SIGNED-IN-01): /api/me carries the chosen company (restored
 *                      before the first call); a refused choice (403 not_a_member) is cleared once and /api/me is asked again
 *                      for the person's own company; 409 company_choice_required routes into the layout, which shows the
 *                      choice; /register opens for a team invite the server confirms, and a signed-in person on a pilot
 *                      or team invite link sees the stop page instead of a silent redirect
 * v3.99 - WOW-RAG-CONTENT-01d: /dashboard/insights index swapped from <Analytics /> to <InsightsFeed /> - 2026-05-05
 * v3.95 - EXCEL-REMOVE-01: remove ReviewQueue + ExcelColumnReview routes and Excel wrappers - 2026-04-19
 * v3.94 - F-01: FeatureFlagProvider + runtime siteMode routing; replace REACT_APP_REGISTRATION_OPEN - 2026-03-20
 * v3.88 - GDPR-03: /legal/cookies route + CookiePolicy page - 2026-03-17
 * v3.87 - GDPR-03: /legal/* routes + LegalLayout + old URL redirects - 2026-03-17
 * v3.86 - 2026-03-16 - PLG: /try route + Try import; /demo-register ungated from REGISTRATION_OPEN
 * v3.82 - 2026-03-15 - GDPR-01+02: /privacy-policy, /terms-of-service routes; CookieConsentBanner
 * v3.79 - 2026-03-14 - PILOT-02: InviteAwareRegisterRoute — ?invite=CODE bypasses REGISTRATION_OPEN gate
 * v3.72 - 2026-03-12 - MKTG-01: /admin route + Admin import (admin marketing dashboard)
 * v3.68 - 2026-03-10 - PILOT-01: Registration gate via REACT_APP_REGISTRATION_OPEN flag; WaitlistPage import + route swaps
 * v3.62 - 2026-03-07 - DEMO-01 round 3: /demo-register route + DemoRegister import
 * v3.61 - 2026-03-06 - DEMO-01: /demo route + isDemo/demoMaxConnections in useClerkAuth
 * v2.83 - 2026-02-05 - OAuthCallback renders immediately (bypasses auth loading gate for popup)
 * v2.80 - 2026-02-05 - OAuth popup flow: /oauth-callback route for clean browser history
 * v2.79 - 2026-02-05 - Added captiveComplete flag for onboarding captive flow routing
 * v2.74 - 2026-02-05 - Use X-Authorization header (SWA intercepts standard Authorization)
 * v2.58 - 2026-02-04 - Added debug logging for Clerk token, include credentials in fetch
 * v2.57 - 2026-02-04 - Fixed re-render loop during signup by removing getToken from useEffect deps
 * v2.56 - 2026-02-04 - Added ClerkTokenProvider to inject session token into API service
 * v2.55 - 2026-02-04 - Migrated to Clerk authentication (added Login, SSOCallback, ForgotPassword routes)
 * v2.49 - 2026-02-03 - Added EULA, Privacy Policy, and Consent pages; /register route
 * v2.46 - 2026-02-03 - Fixed workflowState routing (setup_complete, pipeline_running)
 * v2.22 - 2026-01-25 - Pass workflowState to Onboarding to resume at correct step
 * v2.18 - 2026-01-24 - Chrome HMI fix: AuthLanding page requires user gesture
 * v2.14 - 2026-01-24 - Push TWO states + redirect to home on popstate
 * v2.13 - 2026-01-24 - Moved back button trap to index.html (runs before React)
 * v2.12 - 2026-01-24 - Global back button trap runs immediately on app load
 * v2.11 - 2026-01-24 - Fixed back button trap (only worked after auth check)
 * v2.10 - 2026-01-24 - Added history cleanup after login (insufficient fix)
 * v2.9  - 2026-01-24 - Added LoggedOut page route
 * v2.2  - 2026-01-23 - Initial implementation with onboarding routing
 *
 * IMPORTANT: Azure SWA intercepts and overwrites the standard Authorization header.
 * We use X-Authorization instead. See:
 * - https://github.com/Azure/static-web-apps/issues/158
 * - https://github.com/Azure/static-web-apps/issues/275
 */
import React, { useEffect, useState, useMemo, createContext, Suspense } from 'react'; // v4.34 - WEBSITE-DISABLE-01: useCallback dropped with the theme toggle
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import { createAppTheme } from './theme/theme'; // v2.92 - Dynamic theme creation
import { setClerkTokenGetter } from './services/api'; // v2.56 - Clerk token injection
// v4.29 - ROW 614/615 correction: `teamService` and `rememberReturnPath` are no longer imported
//          here — the InviteAwareRegisterRoute now redirects /register?teamInvite to /invite/<token>
//          and InviteLanding does its own validate and its own return-path handling.
import { failClosedClerkSession, isRejectedClerkSession, setClerkSession } from './services/authGate'; // AUTH-GATE: clear rejected Clerk sessions
import { firstScreenFor, isSetupDone, onboardingStillOpen } from './services/firstScreen'; // v4.29 - ONBOARDING-NOT-ENFORCED-01: one first-screen rule; v4.39 - row 40: onboardingStillOpen
import { useGeoGuard } from './hooks/useGeoGuard'; // v3.85 - MKTG-02: non-US waitlist banner

// Layouts
import { AuthenticatedLayout, PublicLayout, SampleLayout } from './components/layout'; // v3.87 - GDPR-03: LegalLayout removed v3.98 (LegalPageShell owns layout)

// Public Pages
import Landing from './pages/Landing';
import Features from './pages/Features';
import Privacy from './pages/Privacy';
import Eula from './pages/Eula';
import TermsOfService from './pages/TermsOfService'; // v3.82 - GDPR-01: Terms of Service page
import CookiePolicy from './pages/legal/CookiePolicy'; // v3.88 - GDPR-03: Cookie Policy page
import Consent from './pages/Consent';
import Login from './pages/Login'; // v2.55 - Clerk login page
import SSOCallback from './pages/SSOCallback'; // v2.55 - OAuth callback handler
import ForgotPassword from './pages/ForgotPassword'; // v2.55 - Password reset page

// Sample Pages (Demo mode)
import {
  SampleDashboard,
  SampleData,
  SampleInsights,
  SampleAnalytics,
  SampleDashboards,
  SampleReports,
  SampleSettings,
} from './pages/samples';

// Authenticated Pages
import Dashboard from './pages/Dashboard';
import Analytics from './pages/Analytics';
import InsightsFeed from './pages/InsightsFeed'; // v3.99 - WOW-RAG-CONTENT-01d: dedicated /dashboard/insights index page
import Reports from './pages/Reports';
import DataSources from './pages/DataSources';
import DataQuality from './pages/DataQuality'; // v4.06 - [174]: Data Quality page
// v3.16 - Deleted AIInsights (dead code, never routed) - 2016-02-16
import Settings from './pages/Settings';
import Team from './pages/Team';
import Billing from './pages/Billing';
import Onboarding from './pages/Onboarding';
import MemberWaiting from './pages/MemberWaiting'; // v4.29 - ONBOARDING-NOT-ENFORCED-01: a Member of an unfinished company sees this, not onboarding
import LoggedOut from './pages/LoggedOut';
import AuthLanding from './pages/AuthLanding';
import DelegateLanding from './pages/DelegateLanding';
import InviteLanding, { SignedInInviteGate } from './pages/InviteLanding';
import { applyMeResponse, applyChoiceRequired, getMeOutcome, setMeSnapshot } from './hooks/usePermissions'; // v4.28 - G10-B: the one place /api/me is taken in
import ConfirmDeletion from './pages/ConfirmDeletion';
import OAuthCallback from './pages/OAuthCallback'; // v2.80 - OAuth popup callback
import ExportData from './pages/ExportData'; // v2.91 - Export-only page for pending deletion
import Webhooks from './pages/Webhooks'; // v2.98 - Custom webhooks management
import AuditTrail from './pages/AuditTrail'; // v3.94 - MISSED-ID-04 EDGS: employment risk audit trail
import AiTransparency from './pages/AiTransparency'; // v3.94 - MISSED-ID-04 EDGS: AI transparency page
// v4.35 - PILOT-GATE-BROWSER-ONLY-01 (row 737): Demo and DemoRegister imports removed; /demo and /demo-register redirect to "/".
import WaitlistPage from './pages/WaitlistPage'; // v3.68 - PILOT-01: shown when REACT_APP_REGISTRATION_OPEN=false
import About from './pages/About'; // v3.94 - P-01: About page
import Cleanup from './pages/Cleanup'; // v4.36 - EXPO-QUALIFIER-01
import DataSafety from './pages/DataSafety'; // v4.36 - EXPO-QUALIFIER-01
import { FeatureFlagProvider } from './contexts/FeatureFlagContext'; // v3.94 - F-01: Runtime feature flags; v4.35 - PILOT-GATE-BROWSER-ONLY-01: useFeatureFlags no longer read by /register
// v3.89 - ADMIN-01: Admin panel moved to apps/admin/ (admin.mylojic.com) - 2026-03-18
import CookieConsentBanner from './components/CookieConsentBanner'; // v3.82 - GDPR-02: Cookie consent banner
import Try from './pages/Try'; // v3.86 - Story 19 PLG: pre-auth free industry insight tool
import {
  portalRequestUngated, restoreChosenCompany, clearChosenCompany, getChosenCompany, classifyCompanyAccess,
  logClientDecision, COMPANY_NOTICE_STORAGE_KEY,
} from './services/portalRequest'; // v4.28 - G10-B: one request wrapper, carries the chosen company

// v3.36 - Lazy-loaded: Dashboards pulls ~3.5MB Plotly bundle, only on navigation
const Dashboards = React.lazy(() => import('./pages/Dashboards'));

// v4.35 - 2026-10-29 - PILOT-GATE-BROWSER-ONLY-01 (row 737, Branko ruling 2026-10-28): the /register decision does NOT
// depend on the site mode any more (it was: open -> Consent; pilot + ?invite= -> Consent; else WaitlistPage).
// Routing logic, in every site mode:
//   signed in            → unchanged (SignedInInviteGate for an invite link, else /dashboard)
//   ?teamInvite=<token>  → unchanged (/invite/<token>)
//   ?invite=<code>       → Consent, which validates the code with the server before it shows anything
//                          and sends an invalid, used or revoked code to "/"
//   no code              → WaitlistPage (request early access), never Consent
const InviteAwareRegisterRoute: React.FC = () => {
  const [params] = useSearchParams();
  const { isSignedIn } = useAuth();
  const inviteCode = params.get('invite');
  // v4.28 - G10-B (D3): a team invite link opens registration in pilot mode, but only after the server confirms the token
  // v4.29 - 2026-10-13 - ROW 614/615 correction: a team-invite link signed out NEVER renders the
  //          plain owner Consent form; it redirects to /invite/<token> where InviteLanding handles
  //          every branch (ticket, accountExists true/false/absent). Search params carry over so
  //          a __clerk_ticket in the address survives the hop. The former server-side validate +
  //          teamInviteOk state is gone — InviteLanding does its own validate once arrived.
  const teamInvite = params.get('teamInvite');

  // Authenticated users: v4.28 - G10-B (row 609) an invite link never redirects silently. The server says whether the
  // invite is for the signed-in email; a different email gets the stop page.
  if (isSignedIn) {
    if (inviteCode) return <SignedInInviteGate kind="pilot" credential={inviteCode} />;
    if (teamInvite) return <SignedInInviteGate kind="team" credential={teamInvite} />;
    return <Navigate to="/dashboard" replace />;
  }

  // v4.29 - ROW 614/615 correction: team-invite redirect runs regardless of siteMode. An invited
  // person should always land on their invite, not on a waitlist page or an owner sign-up form.
  if (teamInvite) {
    const sp = new URLSearchParams(window.location.search);
    sp.delete('teamInvite');
    const query = sp.toString();
    const target = `/invite/${encodeURIComponent(teamInvite)}${query ? `?${query}` : ''}`;
    return <Navigate to={target} replace />;
  }

  // v4.35 - PILOT-GATE-BROWSER-ONLY-01: no site-mode wait and no site-mode branch. No code -> request early access.
  if (!inviteCode) return <WaitlistPage />;
  return <Consent inviteCode={inviteCode} />;
};

// v2.92 - Theme context for dark mode toggle (must be after all imports for ESLint import/first)
export const ThemeContext = createContext<{
  mode: 'light' | 'dark';
  toggleTheme: () => void;
}>({ mode: 'light', toggleTheme: () => {} });

// v4.34 - WEBSITE-DISABLE-01 (row 781) - the one value the provider gives: light only, no-op toggle.
const LIGHT_THEME: { mode: 'light'; toggleTheme: () => void } = { mode: 'light', toggleTheme: () => {} };

// v2.56 - Clerk auth hook replacing SWA auth, now injects token getter for API service
// v3.61 - DEMO-01: exported so DataSourceConnect + Onboarding can destructure isDemo/demoMaxConnections
// v4.28 - G10-B: signed in, but the company is not known yet (choice required, a pending invite, or a lookup to retry).
// Routing sends the person to the dashboard layout, which shows the right screen; nothing of any company is rendered.
const companyUnknownState = (user: any) => ({
  isAuthenticated: true,
  isLoading: false,
  user: { id: user.id, email: user.primaryEmailAddress?.emailAddress },
  tenantId: null,
  workflowState: 'setup_complete',
  role: null,
  captiveComplete: 'done',
  accountStatus: 'active',
  deletionScheduledAt: null,
  isDemo: false,
  demoMaxConnections: 3,
  connectSkipped: false, // v4.39 - row 40
});

export const useClerkAuth = () => {
  const { isSignedIn, isLoaded: authLoaded, getToken, signOut, sessionId } = useAuth();
  const { user, isLoaded: userLoaded } = useUser();
  const [authState, setAuthState] = useState<{
    isAuthenticated: boolean;
    isLoading: boolean;
    user: any;
    tenantId: string | null;
    workflowState: string | null;
    role: string | null;
    captiveComplete: string | null;  // v2.79 - Captive onboarding flag
    accountStatus: string;                    // v2.91 - Deletion lifecycle
    deletionScheduledAt: string | null;       // v2.91
    isDemo: boolean;                          // v3.61 - DEMO-01
    demoMaxConnections: number;               // v3.61 - DEMO-01
    connectSkipped: boolean;                  // v4.39 - BATCH-2026-10-31 row 40: /api/me connectSkipped
  }>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    tenantId: null,
    workflowState: null,
    role: null,
    captiveComplete: null,
    accountStatus: 'active',
    deletionScheduledAt: null,
    isDemo: false,
    demoMaxConnections: 3,
    connectSkipped: false, // v4.39 - row 40
  });

  useEffect(() => {
    setClerkSession(signOut, sessionId || null);
  }, [signOut, sessionId]);

  // v2.56 - Set up Clerk token getter for API service
  useEffect(() => {
    if (authLoaded && getToken) {
      setClerkTokenGetter(async () => {
        try {
          return await getToken();
        } catch {
          return null;
        }
      });
    }
  }, [authLoaded, getToken]);

  useEffect(() => {
    let active = true;
    const fetchUserDetails = async () => {
      if (!authLoaded || !userLoaded) return;

      if (!isSignedIn || !user || isRejectedClerkSession(sessionId)) {
        setMeSnapshot(null); // v4.28 - G10-B: nothing of a previous person survives sign-out
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          tenantId: null,
          workflowState: null,
          role: null,
          captiveComplete: null,
          accountStatus: 'active',
          deletionScheduledAt: null,
          isDemo: false,
          demoMaxConnections: 3,
          connectSkipped: false, // v4.39 - row 40
        });
        return;
      }

      // User is signed in with Clerk, now fetch additional details from our backend
      try {
        // v2.58 - Get Clerk session token for API authentication
        // getToken() returns the session JWT that BFF can verify
        const token = await getToken();
        console.log('Clerk token obtained:', token ? 'yes (length: ' + token.length + ')' : 'null');

        if (!token) {
          console.warn('No Clerk token available, user may need to re-authenticate');
        }

        // v2.74 - Use X-Authorization instead of Authorization
        // Azure SWA intercepts Authorization header and replaces it with its own internal token
        // See: https://github.com/Azure/static-web-apps/issues/158
        // v4.28 - G10-B: the person's last company choice goes on this very first request
        restoreChosenCompany(user.id);
        const askMe = () => portalRequestUngated('/api/me', {
          headers: token ? { 'X-Authorization': `Bearer ${token}` } : {},
          credentials: 'include', // v2.58 - Include cookies as fallback
        });
        let meResponse = await askMe();
        // v4.28 - G10-B: the chosen company is no longer allowed (403 not_a_member): clear it once, say why on the
        // next screen, and ask again for the person's own company. No silent fallback: the notice is shown.
        if (meResponse.status === 403 && getChosenCompany()) {
          const refusal = await meResponse.clone().json().catch(() => null);
          if (classifyCompanyAccess(403, refusal) === 'not_a_member') {
            logClientDecision('not_a_member_choice_cleared', { company: getChosenCompany() || 'none', route: '/api/me' });
            clearChosenCompany();
            try { sessionStorage.setItem(COMPANY_NOTICE_STORAGE_KEY, 'not_a_member'); } catch { /* optional */ }
            meResponse = await askMe();
          }
        }
        const _applyMeData = (meData: Record<string, unknown>) => {
          if (!active) return;
          if (isRejectedClerkSession(sessionId)) return;
          // v4.28 - G10-B: role + permissions of the chosen company go to the one shared store (a role is never guessed)
          const applied = applyMeResponse(meData, user.id);
          // A person with no company and a pending team invite (200, role null, pendingInvite): the layout takes them to the invite.
          if (!applied && getMeOutcome() === 'pending_invite') {
            setAuthState(companyUnknownState(user));
            return;
          }
          setAuthState({
            isAuthenticated: true,
            isLoading: false,
            user: {
              id: user.id,
              email: user.primaryEmailAddress?.emailAddress,
              firstName: user.firstName,
              lastName: user.lastName,
              ...meData,
            },
            tenantId: meData.tenantId as string | null,
            workflowState: (meData.workflowState as string) || 'signed_up',
            role: (meData.role as string) || 'member',
            captiveComplete: (meData.captiveComplete as string | null) || null,  // v2.79 - Captive flag
            accountStatus: (meData.status as string) || 'active',                // v2.91 - Deletion lifecycle
            deletionScheduledAt: (meData.deletionScheduledAt as string | null) || null, // v2.91
            isDemo: (meData.is_demo as boolean) || false,                        // v3.61 - DEMO-01
            demoMaxConnections: (meData.demo_max_connections as number) || 3,    // v3.61 - DEMO-01
            connectSkipped: meData.connectSkipped === true,                      // v4.39 - row 40
          });
        };

        if (!active) return;
        if (meResponse.ok) {
          const meData = await meResponse.json();
          _applyMeData(meData);
          return;
        }

        // v4.28 - G10-B: 409 company_choice_required - the body carries the companies; the layout shows the choice.
        // Routing treats the person as past onboarding because the company is not known yet; the layout never renders
        // company data before the choice.
        if (meResponse.status === 409) {
          const choiceBody = await meResponse.clone().json().catch(() => null);
          if (classifyCompanyAccess(409, choiceBody) === 'company_choice_required' && active) {
            applyChoiceRequired(choiceBody);
            setAuthState(companyUnknownState(user));
            return;
          }
          // v4.30 - ROW 69 (ruling 10): the company record has no workflow state. The layout says so; no company data is shown.
          if (classifyCompanyAccess(409, choiceBody) === 'setup_incomplete' && active) {
            logClientDecision('setup_incomplete', { company: getChosenCompany() || 'none', route: '/api/me' });
            setMeSnapshot(null, 'setup_incomplete');
            setAuthState(companyUnknownState(user));
            return;
          }
        }

        // v4.28 - G10-B: 503 on a company, role, companies or invite lookup is a "try again", not a sign-out.
        if (meResponse.status === 503) {
          const unavailableBody = await meResponse.clone().json().catch(() => null);
          if (classifyCompanyAccess(503, unavailableBody) === 'unavailable' && active) {
            logClientDecision('me_unavailable', { company: getChosenCompany() || 'none' });
            setMeSnapshot(null, 'unavailable');
            setAuthState(companyUnknownState(user));
            return;
          }
        }

        // A rejected JWT must clear Clerk before navigation; otherwise Clerk can
        // immediately redirect the stale session back into the protected portal.
        if (meResponse.status === 401) {
          await failClosedClerkSession(signOut, undefined, sessionId || null);
          return;
        }

        // v3.96 - AUTH-SELF-HEAL-01: attempt recovery once on 404 (missing tenant state).
        // sessionStorage guard prevents retry/redirect loops — at most one attempt per load.
        if (meResponse.status === 404 && !sessionStorage.getItem('auth_heal_attempted')) {
          try {
            sessionStorage.setItem('auth_heal_attempted', '1');
            const healResponse = await portalRequestUngated('/api/me/recover', {
              method: 'POST',
              headers: token ? { 'X-Authorization': `Bearer ${token}` } : {},
              credentials: 'include',
            });
            if (healResponse.ok) {
              const healData = await healResponse.json();
              if (healData.recovered) {
                // Recovery succeeded — retry /api/me once
                const retryResponse = await portalRequestUngated('/api/me', {
                  headers: token ? { 'X-Authorization': `Bearer ${token}` } : {},
                  credentials: 'include',
                });
                if (retryResponse.ok) {
                  const retryData = await retryResponse.json();
                  _applyMeData(retryData);
                  return;
                }
              }
            }
          } catch (healError) {
            console.warn('Auth self-heal attempt failed:', healError);
          }
        }

        // A 404 may self-heal exactly once. If recovery or the one retry does not
        // produce a valid /api/me response, fail closed instead of authenticating
        // from Clerk-only data with no tenant context.
        if (meResponse.status === 404) {
          await failClosedClerkSession(signOut);
          return;
        }
      } catch (meError) {
        console.error('Failed to fetch user details:', meError);
      }

      if (!active) return;
      // Protected routes require a successful backend identity response.
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        tenantId: null,
        workflowState: null,
        role: null,
        captiveComplete: null,
        accountStatus: 'active',
        deletionScheduledAt: null,
        isDemo: false,
        demoMaxConnections: 3,
        connectSkipped: false, // v4.39 - row 40
      });
    };

    fetchUserDetails();
    return () => { active = false; };
  // v4.00 - Depend on stable Clerk user ID, not the mutable user object reference.
  // The object can be recreated after setAuthState, which previously caused an unbounded /api/me loop.
  // getToken remains intentionally excluded because its function identity can also change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoaded, userLoaded, isSignedIn, user?.id, sessionId]);

  return authState;
};

const AppRoutes: React.FC = () => {
  // v2.79 - Added captiveComplete for onboarding captive flow
  // v2.91 - Added accountStatus for deletion lifecycle routing
  // v3.61 - DEMO-01: added isDemo, demoMaxConnections
  // v3.85 - MKTG-02: geo guard for non-US waitlist banner
  // v4.29 - ONBOARDING-NOT-ENFORCED-01: role is now read here so a non-owner of an unfinished
  //         company gets MemberWaiting, not Onboarding (A.2 in the dispatch).
  const { isAuthenticated, isLoading, user, tenantId, workflowState, captiveComplete, role, accountStatus, deletionScheduledAt, isDemo, connectSkipped } = useClerkAuth(); // v4.39 - row 40: connectSkipped
  const { showWaitlistBanner, detectedCountry } = useGeoGuard();

  // v4.29 - ONBOARDING-NOT-ENFORCED-01: one decider for every signed-in redirect; the former
  // isInCaptiveFlow / hasDoneCaptive / isOnboarding list and the free `?setup=complete` bypass
  // are gone. `setupJustCompleted` is honoured only when /api/me already reports setup done
  // (A.3): a stale Cosmos read that the parameter was added to bridge (v3.00) only matters
  // once the server actually says the setup is done.
  const firstScreen = firstScreenFor({ captiveComplete, workflowState, connectSkipped });
  const setupDone = isSetupDone({ captiveComplete, workflowState });
  // v4.39 - row 40: a company that skipped Connect your data lands on the dashboard; its owner may still open
  // /onboarding to connect a source (the OAuth return and Finish Setup live there).
  const onboardingOpen = onboardingStillOpen({ captiveComplete, workflowState, connectSkipped });
  const urlParams = new URLSearchParams(window.location.search);
  const setupJustCompleted = urlParams.get('setup') === 'complete' && setupDone;
  const isOwner = role === 'owner';

  // v2.83 - OAuthCallback must render immediately in popup (no auth wait)
  // The popup just needs to send postMessage to parent and close itself.
  // Waiting for Clerk + /api/me would delay the message and break the refresh.
  // v4.19 - AIRBYTE-BRIDGE-PLATFORM-CREDENTIALS S3 (F04): early server failures
  // redirect to the hardcoded /connections?error=... target; route it to the same
  // OAuthCallback (both trees) so the error reaches the handler instead of the
  // catch-all (which drops the query string).
  if (window.location.pathname === '/oauth-callback' || window.location.pathname === '/connections') {
    return (
      <Routes>
        <Route path="/oauth-callback" element={<OAuthCallback />} />
        <Route path="/connections" element={<OAuthCallback />} />
      </Routes>
    );
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Routes>
      {/* PUBLIC ROUTES */}
      <Route element={<PublicLayout />}>
        {/* v2.79 - captiveComplete check is the primary routing gate */}
        {/* v2.91 - pending_deletion check added */}
        <Route
          path="/"
          element={
            isAuthenticated ? (
              accountStatus === 'pending_deletion' ? (
                <Navigate to="/export-data" replace />
              ) : (
                // v4.29 - ONBOARDING-NOT-ENFORCED-01: one rule in firstScreenFor
                <Navigate to={firstScreen} replace />
              )
            ) : (
              <Landing showWaitlistBanner={showWaitlistBanner} detectedCountry={detectedCountry} />
            )
          }
        />
        <Route path="/features" element={<Features />} />
        {/* v3.94 - /pricing redirects to landing page #pricing anchor; /about has dedicated page */}
        <Route path="/pricing" element={<Navigate to="/" replace />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Landing />} />
      </Route>

      {/* v4.36 - EXPO-QUALIFIER-01: public pages with the banner but no Sign In and no early-access button */}
      <Route element={<PublicLayout hideAuth />}>
        <Route path="/cleanup" element={<Cleanup />} />
        <Route path="/data-safety" element={<DataSafety />} />
      </Route>

      {/* v3.98 - LegalPageShell owns layout for all legal pages; LegalLayout wrapper removed */}
      <Route path="/legal/privacy" element={<Privacy />} />
      <Route path="/legal/terms" element={<TermsOfService />} />
      <Route path="/legal/eula" element={<Eula />} />
      <Route path="/legal/cookies" element={<CookiePolicy />} />

      {/* v3.87 - GDPR-03: Redirect old legal URLs to /legal/* */}
      <Route path="/privacy-policy" element={<Navigate to="/legal/privacy" replace />} />
      <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
      <Route path="/terms-of-service" element={<Navigate to="/legal/terms" replace />} />
      <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
      <Route path="/eula" element={<Navigate to="/legal/eula" replace />} />

      {/* v2.91 - EXPORT-ONLY ROUTE for pending_deletion accounts */}
      <Route
        path="/export-data"
        element={
          isAuthenticated && accountStatus === 'pending_deletion' ? (
            <ExportData deletionScheduledAt={deletionScheduledAt} />
          ) : isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* v2.55 - CLERK AUTH ROUTES */}
      <Route path="/login" element={<Login />} />
      {/* v3.79 - PILOT-02: invite=CODE bypasses gate; v3.68 - PILOT-01: gate otherwise */}
      <Route path="/register" element={<InviteAwareRegisterRoute />} />
      {/* v4.35 - PILOT-GATE-BROWSER-ONLY-01 (row 737, Branko ruling 2026-10-28): the demo funnel is closed. /demo (with or
          without ?code=) and /demo-register redirect to "/"; no account is created there. */}
      <Route path="/demo" element={<Navigate to="/" replace />} />
      <Route path="/demo-register" element={<Navigate to="/" replace />} />
      {/* v3.86 - Story 19 PLG: pre-auth free industry insight tool */}
      <Route path="/try" element={<Try />} />
      <Route path="/sso-callback" element={<SSOCallback />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />

      {/* LOGGED OUT ROUTE - Shown after logout */}
      <Route path="/logged-out" element={<LoggedOut />} />

      {/* v2.80 - OAUTH CALLBACK ROUTE - For popup OAuth flow */}
      <Route path="/oauth-callback" element={<OAuthCallback />} />
      {/* v4.19 - AIRBYTE-BRIDGE-PLATFORM-CREDENTIALS S3 (F04): early server failure
          target /connections?error=... routes to the same handler (main tree). */}
      <Route path="/connections" element={<OAuthCallback />} />

      {/* AUTH LANDING ROUTE - Chrome HMI fix: requires user click before proceeding */}
      <Route path="/auth-landing" element={<AuthLanding />} />

      {/* v2.26 - DELEGATION ROUTE - For delegates connecting data sources */}
      <Route path="/delegate/:token" element={<DelegateLanding />} />

      {/* v2.31 - TEAM INVITE ROUTE - For accepting team invitations */}
      <Route path="/invite/:token" element={<InviteLanding />} />

      {/* v2.31 - DELETION CONFIRMATION ROUTE - For confirming account deletion */}
      <Route path="/settings/confirm-deletion/:token" element={<ConfirmDeletion />} />

      {/* ONBOARDING ROUTE - For new users */}
      {/* v2.79 - captiveComplete="done" means user can never see onboarding again */}
      {/* v4.29 - ONBOARDING-NOT-ENFORCED-01: firstScreenFor decides whether /onboarding renders;
          a non-owner of an unfinished company sees MemberWaiting, not the owner's steps. */}
      <Route
        path="/onboarding"
        element={
          isAuthenticated ? (
            firstScreen === '/dashboard' && !(isOwner && onboardingOpen) ? (
              <Navigate to="/dashboard" replace />
            ) : isOwner ? (
              <Onboarding tenantId={tenantId || ''} email={user?.email || ''} workflowState={workflowState || undefined} captiveComplete={captiveComplete || undefined} isDemo={isDemo} />
            ) : (
              <MemberWaiting />
            )
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* SAMPLE/DEMO ROUTES - Public preview with full layout */}
      <Route path="/samples" element={<SampleLayout />}>
        <Route index element={<SampleDashboard />} />
        <Route path="data" element={<SampleData />} />
        <Route path="insights">
          <Route index element={<SampleInsights />} />
          <Route path="analytics" element={<SampleAnalytics />} />
          <Route path="reports" element={<SampleReports />} />
          <Route path="dashboards" element={<SampleDashboards />} />
        </Route>
        <Route path="config">
          <Route index element={<SampleSettings />} />
          <Route path="settings" element={<SampleSettings />} />
          <Route path="team" element={<SampleSettings />} />
          <Route path="billing" element={<SampleSettings />} />
        </Route>
      </Route>

      {/* AUTHENTICATED ROUTES - Redirect to login if not authenticated */}
      {/* v2.79 - captiveComplete guards: in_progress forces onboarding, done allows dashboard */}
      {/* v2.91 - pending_deletion users are routed to export-only page */}
      {/* v4.29 - ONBOARDING-NOT-ENFORCED-01: firstScreenFor decides; `?setup=complete` is honoured
          only when /api/me already reports setup done (setupJustCompleted computed above). */}
      <Route
        path="/dashboard"
        element={
          !isAuthenticated ? (
            <Navigate to="/login" replace />
          ) : accountStatus === 'pending_deletion' ? (
            <Navigate to="/export-data" replace />
          ) : !setupJustCompleted && firstScreen === '/onboarding' ? (
            <Navigate to="/onboarding" replace />
          ) : (
            <AuthenticatedLayout />
          )
        }
      >
        <Route index element={<Dashboard />} />

        {/* Data Section */}
        <Route path="data" element={<DataSources />} />
        <Route path="data/quality" element={<DataQuality />} /> {/* v4.06 - [174] */}

        {/* Insights Section */}
        <Route path="insights">
          {/* v3.99 - WOW-RAG-CONTENT-01d: dedicated full-feed page (was <Analytics />) */}
          <Route index element={<InsightsFeed />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="reports" element={<Reports />} />
          <Route path="dashboards" element={<Suspense fallback={<div />}><Dashboards /></Suspense>} />
        </Route>

        {/* Config Section */}
        <Route path="config">
          <Route index element={<Settings />} />
          <Route path="settings" element={<Settings />} />
          <Route path="team" element={<Team />} />
          <Route path="webhooks" element={<Webhooks />} />
          <Route path="billing" element={<Billing />} />
          <Route path="audit-trail" element={<AuditTrail />} /> {/* v3.94 - MISSED-ID-04 EDGS */}
        </Route>

        {/* Legacy routes - redirect to new structure */}
        <Route path="analytics" element={<Navigate to="/dashboard/insights/analytics" replace />} />
        <Route path="reports" element={<Navigate to="/dashboard/insights/reports" replace />} />
        <Route path="dashboards" element={<Navigate to="/dashboard/insights/dashboards" replace />} />
        <Route path="settings" element={<Navigate to="/dashboard/config/settings" replace />} />
        <Route path="team" element={<Navigate to="/dashboard/config/team" replace />} />
        <Route path="billing" element={<Navigate to="/dashboard/config/billing" replace />} />
      </Route>

      {/* Legacy routes for old flat structure - redirect to new */}
      <Route path="/analytics" element={<Navigate to="/dashboard/insights/analytics" replace />} />
      <Route path="/reports" element={<Navigate to="/dashboard/insights/reports" replace />} />
      <Route path="/data-sources" element={<Navigate to="/dashboard/data" replace />} />
      <Route path="/dashboards" element={<Navigate to="/dashboard/insights/dashboards" replace />} />
      <Route path="/ai-insights" element={<Navigate to="/dashboard" replace />} />
      <Route path="/settings" element={<Navigate to="/dashboard/config/settings" replace />} />
      <Route path="/team" element={<Navigate to="/dashboard/config/team" replace />} />
      <Route path="/billing" element={<Navigate to="/dashboard/config/billing" replace />} />

      {/* v3.89 - ADMIN-01: /admin moved to apps/admin/ (admin.mylojic.com) */}

      {/* v3.94 - MISSED-ID-04 EDGS: AI Transparency page (public, no auth required) */}
      <Route path="/ai-transparency" element={<AiTransparency />} />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  // Back button trap is now in index.html (runs before React loads)

  // v4.34 - WEBSITE-DISABLE-01 (row 781) - 2026-10-28 - Dark mode removed for customers (Branko):
  // the portal always renders the light theme. The saved browser setting 'themeMode' is ignored and
  // removed on load; the context always provides 'light' and its toggle does nothing. (Was v3.96:
  // dark by default for first-time visitors, stored preference respected otherwise.)
  useEffect(() => {
    try {
      localStorage.removeItem('themeMode');
    } catch {
      // storage blocked (private window / blocked site data): nothing saved to remove
    }
  }, []);

  const theme = useMemo(() => createAppTheme(LIGHT_THEME.mode), []);

  return (
    <ThemeContext.Provider value={LIGHT_THEME}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {/* v3.94 - F-01: FeatureFlagProvider wraps all routes — public and authenticated */}
        <FeatureFlagProvider>
          <Router>
            <AppRoutes />
            <CookieConsentBanner />
          </Router>
        </FeatureFlagProvider>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default App;
